import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { LogOut, Sparkles, FileText, Video, Gem, Music, Image as ImageIcon, Quote } from 'lucide-react';
import { adminMe, adminLogout } from '../../services/api';
import BlogManager from './BlogManager';
import VideoManager from './VideoManager';
import CrystalManager from './CrystalManager';
import TestimonialsManager from './TestimonialsManager';
import MusicManager from './MusicManager';
import LogoManager from './LogoManager';
import { Fireflies } from '../../components/FairyElements';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminMe()
      .then(setUser)
      .catch(() => navigate('/admin'))
      .finally(() => setLoading(false));
  }, [navigate]);

  const handleLogout = async () => {
    await adminLogout();
    navigate('/admin');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a1612] text-amber-200">
        Loading...
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#0a1612] relative">
      <Fireflies count={25} />

      {/* Top bar */}
      <div className="sticky top-0 z-30 bg-[#0a1612]/95 backdrop-blur-md border-b border-amber-600/30">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-700 to-amber-600 rounded-lg flex items-center justify-center mystical-glow-gold">
              <Sparkles className="w-5 h-5 text-amber-100" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-amber-200" style={{ fontFamily: 'Cinzel, serif' }}>
                Admin Dashboard
              </h1>
              <p className="text-xs text-amber-100/60">Welcome back, {user.name || user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              className="text-amber-300 hover:text-amber-200 hover:bg-emerald-900/40"
              onClick={() => navigate('/')}
              data-testid="view-site-btn"
            >
              View Site
            </Button>
            <Button
              onClick={handleLogout}
              className="bg-red-900/40 text-red-300 border border-red-500/30 hover:bg-red-900/60"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 relative z-10">
        <Tabs defaultValue="blog" className="w-full">
          <TabsList className="bg-emerald-950/60 border border-amber-600/30 p-1 mb-8 flex-wrap h-auto">
            <TabsTrigger
              value="blog"
              className="data-[state=active]:bg-amber-600 data-[state=active]:text-emerald-950 text-amber-200 px-4 md:px-6"
              data-testid="tab-blog"
            >
              <FileText className="w-4 h-4 mr-2" />
              Blog
            </TabsTrigger>
            <TabsTrigger
              value="videos"
              className="data-[state=active]:bg-amber-600 data-[state=active]:text-emerald-950 text-amber-200 px-4 md:px-6"
              data-testid="tab-videos"
            >
              <Video className="w-4 h-4 mr-2" />
              Videos
            </TabsTrigger>
            <TabsTrigger
              value="crystals"
              className="data-[state=active]:bg-amber-600 data-[state=active]:text-emerald-950 text-amber-200 px-4 md:px-6"
              data-testid="tab-crystals"
            >
              <Gem className="w-4 h-4 mr-2" />
              Crystals
            </TabsTrigger>
            <TabsTrigger
              value="testimonials"
              className="data-[state=active]:bg-amber-600 data-[state=active]:text-emerald-950 text-amber-200 px-4 md:px-6"
              data-testid="tab-testimonials"
            >
              <Quote className="w-4 h-4 mr-2" />
              Testimonials
            </TabsTrigger>
            <TabsTrigger
              value="music"
              className="data-[state=active]:bg-amber-600 data-[state=active]:text-emerald-950 text-amber-200 px-4 md:px-6"
              data-testid="tab-music"
            >
              <Music className="w-4 h-4 mr-2" />
              Music
            </TabsTrigger>
            <TabsTrigger
              value="logo"
              className="data-[state=active]:bg-amber-600 data-[state=active]:text-emerald-950 text-amber-200 px-4 md:px-6"
              data-testid="tab-logo"
            >
              <ImageIcon className="w-4 h-4 mr-2" />
              Logo
            </TabsTrigger>
          </TabsList>

          <TabsContent value="blog">
            <Card className="forest-card border-0">
              <CardContent className="p-6">
                <BlogManager />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="videos">
            <Card className="forest-card border-0">
              <CardContent className="p-6">
                <VideoManager />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="crystals">
            <Card className="forest-card border-0">
              <CardContent className="p-6">
                <CrystalManager />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="testimonials">
            <Card className="forest-card border-0">
              <CardContent className="p-6">
                <TestimonialsManager />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="music">
            <Card className="forest-card border-0">
              <CardContent className="p-6">
                <MusicManager />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="logo">
            <Card className="forest-card border-0">
              <CardContent className="p-6">
                <LogoManager />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
